import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import { EPISODE_TAG_NONE } from "./datasetEpisode";
import type { DatasetStorageBackend } from "./datasetStorageBackend";

/**
 * Dataset 목록 필터의 선택지별 데이터셋 수(`GET /v1/datasets/facets`).
 *
 * 모집단은 목록 기본 스코프(조직 + available + 반입 seed 제외)에 `?folder=` 와
 * `?storage_backend=` 게이트만 추가한 것이다. 그래서 `total` 은 같은 게이트의 목록 total 과 같다.
 * 선택지의 `count` 는 **그 토큰 하나로** 거른 목록의 total 과 같다. 이 수는 다른 활성 필터와 독립이다. 그래서 필터가 바뀌어도
 * 다시 조회하지 않는다. 보관됨·북마크·리뷰 큐는 모집단이 달라서 이 수를 쓸 수 없다.
 *
 * 서버는 축마다 모든 선택지를 포함한다. 값이 0 인 선택지도 포함한다. 선택지 목록·문구·순서는
 * FE 필터 카탈로그가 소유한다. 이 모듈은 **값으로 조인해 개수만** 가져다 쓴다.
 */

/** 값 축의 선택지 하나다. `value` 는 목록 쿼리 토큰과 같다. 예) `{value: "2", count: 805}` */
export type DatasetFacetOption = {
  value: string;
  count: number;
};

/**
 * 카탈로그 축(embodiment · end_effector · tip)의 한 행이다. 같은 `name` 이 tag 만 다른 여러 행으로
 * 올 수 있다. 그래서 화면은 name 으로 행을 합쳐 합산한다(목록 쿼리 토큰이 name 이다).
 * sentinel `unknown` 행은 여기에 없고 `unspecified` 에 들어간다.
 */
export type DatasetCatalogFacetOption = {
  uuid: string;
  name: string;
  tag: string | null;
  count: number;
};

/**
 * 축 하나다. `unspecified` 는 그 축 값이 없는 데이터셋 수다. 이 묶음을 고르는 토큰은 축마다 다르다:
 * `<key>=unlabeled`(라벨 축) · `unknown`(카탈로그 축·arms) · `camera_count=0`(카메라 축) ·
 * `episode_quality=none` · `action_source=none`. recording_mode·camera_count 는 토큰이 모집단을 빈틈없이 나누므로 항상 0 이다.
 */
export type DatasetFacetAxis<Option = DatasetFacetOption> = {
  options: readonly Option[];
  unspecified: number;
};

export type DatasetFacets = {
  total: number;
  source: DatasetFacetAxis;
  environment: DatasetFacetAxis;
  task: DatasetFacetAxis;
  embodiment: DatasetFacetAxis<DatasetCatalogFacetOption>;
  end_effector: DatasetFacetAxis<DatasetCatalogFacetOption>;
  tip: DatasetFacetAxis<DatasetCatalogFacetOption>;
  arms: DatasetFacetAxis;
  recording_mode: DatasetFacetAxis;
  camera_count: DatasetFacetAxis;
  camera_channel: DatasetFacetAxis;
  camera_mount: DatasetFacetAxis;
  // 에피소드 결함 값마다 "그 결함이 있는 에피소드가 하나라도 있는 데이터셋" 의 수다.
  // 전역 제외한 에피소드의 결함은 세지 않는다. `unspecified` 는 결함 에피소드가 없는
  // 데이터셋이다(`none` 토큰).
  episode_quality: DatasetFacetAxis;
  // @compat ZERG-937 until=be:be56ea52
  episode_quality_candidate?: DatasetFacetAxis;
  // 제외되지 않은 에피소드의 행동 출처다. 한 데이터셋이 여러 값을 가질 수 있다.
  // unspecified는 기록된 값이 없는 수이며 none으로 고른다.
  action_source: DatasetFacetAxis;
};

/**
 * 축의 `unspecified` 묶음을 목록에서 고르는 토큰이다. 카메라 축(camera_count · camera_channel ·
 * camera_mount)의 미지정은 `camera_count` 의 `"0"` 선택지가 이미 담당한다. 그래서 여기에 없다.
 * recording_mode 는 토큰이 모집단을 빈틈없이 나누므로 미지정이 없다.
 */
export const DATASET_FACET_UNSPECIFIED_TOKENS: Readonly<Record<string, string>> = {
  source: "unlabeled",
  environment: "unlabeled",
  task: "unlabeled",
  embodiment: "unknown",
  end_effector: "unknown",
  tip: "unknown",
  arms: "unknown",
  episode_quality: EPISODE_TAG_NONE,
  episode_quality_candidate: "none",
  action_source: "none",
};

// facets 가 받는 게이트다. 목록의 같은 게이트와 값이 같아야 개수가 목록 total 과 맞는다.
export type DatasetFacetGates = {
  // 목록의 `?folder=` 와 같은 토큰이다(폴더 uuid 또는 `unfiled`). null 이면 폴더로 거르지 않는다.
  folder: string | null;
  // 목록의 `?storage_backend=` 와 같은 값이다. null 이면 저장 위치로 거르지 않는다.
  storageBackend: DatasetStorageBackend | null;
};

/**
 * 게이트 안의 축별 개수를 조회한다. 선택지의 `count` 는 같은 게이트에 그 토큰 하나를 더해 거른
 * 목록의 `total` 과 같다.
 *
 * 결과를 캐시하지 않는다. 업로드·보관·폴더 이동마다 값이 바뀐다. 목록 mutation 을 아는 호출자가
 * 이 함수를 다시 호출해 값을 갱신한다.
 */
export async function getDatasetFacets(gates: DatasetFacetGates): Promise<DatasetFacets> {
  const response = await apiClient.get<DatasetFacets>(apiRoutes.dataset.facets, {
    params: {
      ...(gates.folder === null ? {} : { folder: gates.folder }),
      ...(gates.storageBackend === null ? {} : { storage_backend: gates.storageBackend }),
    },
  });
  return response.data;
}
